/**
 * App root. Connects to Discord (or the Mock SDK), renders the review screen, and handles
 * submission. Rating-only is one-shot. A full review goes:
 *   form → compose (/api/review/draft) → "Review & refine" → confirm (/api/review/confirm) → result.
 * Nothing posts until the member taps "Post it" on the refine screen.
 */
import { useState } from 'react';
import type { ReviewResponse } from '@pghbites/shared';
import { useActivity } from './state/useActivity.js';
import { useReviewForm } from './state/useReviewForm.js';
import { launchParams } from './sdk/index.js';
import { submitReview, composeDraft, confirmReview } from './api.js';
import { ReviewScreen } from './components/ReviewScreen.js';
import { RefineScreen } from './components/RefineScreen.js';
import { ResultScreen } from './components/ResultScreen.js';

type Submission =
  | { phase: 'form'; error: string | null }
  | { phase: 'submitting' } // rating-only one-shot
  | { phase: 'composing' } // waiting for the first draft
  | { phase: 'refine'; draft: string; rewriting: boolean; posting: boolean; error: string | null }
  | { phase: 'done'; response: ReviewResponse };

export function App() {
  const state = useActivity();
  const [formKey, setFormKey] = useState(0);
  const form = useReviewForm(launchParams());
  const [submission, setSubmission] = useState<Submission>({ phase: 'form', error: null });

  // The submitting Discord user id is derived server-side from the verified session (the Bearer
  // token set in useActivity), NOT sent from here — so we submit the form payload as-is.
  const withAuthor = () => form.review;

  // ── Rating-only: one-shot, unchanged ────────────────────────────────────────
  const postRating = async () => {
    setSubmission({ phase: 'submitting' });
    try {
      const response = await submitReview('rating', withAuthor());
      setSubmission(response.ok ? { phase: 'done', response } : { phase: 'form', error: response.message });
    } catch {
      setSubmission({ phase: 'form', error: 'Could not reach the server. Please try again.' });
    }
  };

  // ── Full review: compose first, then refine, then confirm ───────────────────
  const startReview = async () => {
    setSubmission({ phase: 'composing' });
    try {
      const res = await composeDraft(withAuthor());
      if (res.ok && res.draft != null) {
        setSubmission({ phase: 'refine', draft: res.draft, rewriting: false, posting: false, error: null });
      } else {
        setSubmission({ phase: 'form', error: res.message ?? 'Could not compose a draft.' });
      }
    } catch {
      setSubmission({ phase: 'form', error: 'Could not reach the server. Please try again.' });
    }
  };

  if (state.status === 'connecting') {
    return (
      <main className="center-screen" aria-busy="true">
        <p className="muted">Connecting to Discord…</p>
      </main>
    );
  }
  if (state.status === 'error') {
    return (
      <main className="center-screen" role="alert">
        <p>We couldn’t connect.</p>
        <p className="muted">{state.message}</p>
      </main>
    );
  }

  if (submission.phase === 'composing') {
    return (
      <main className="center-screen" aria-busy="true">
        <p>Writing your review…</p>
        <p className="muted">You’ll get to review and edit it before anything posts.</p>
      </main>
    );
  }

  if (submission.phase === 'refine') {
    const refine = submission;
    const onRewrite = async () => {
      setSubmission((s) => (s.phase === 'refine' ? { ...s, rewriting: true, error: null } : s));
      try {
        const res = await composeDraft(withAuthor());
        setSubmission((s) => {
          if (s.phase !== 'refine') return s;
          if (res.ok && res.draft != null) return { ...s, draft: res.draft, rewriting: false };
          return { ...s, rewriting: false, error: res.message ?? 'Could not rewrite the draft.' };
        });
      } catch {
        setSubmission((s) => (s.phase === 'refine' ? { ...s, rewriting: false, error: 'Could not reach the server.' } : s));
      }
    };
    const onPost = async () => {
      const draft = refine.draft;
      setSubmission((s) => (s.phase === 'refine' ? { ...s, posting: true, error: null } : s));
      try {
        const res = await confirmReview(withAuthor(), draft);
        if (res.ok) setSubmission({ phase: 'done', response: res });
        else setSubmission((s) => (s.phase === 'refine' ? { ...s, posting: false, error: res.message } : s));
      } catch {
        setSubmission((s) => (s.phase === 'refine' ? { ...s, posting: false, error: 'Could not reach the server.' } : s));
      }
    };
    return (
      <RefineScreen
        review={form.review}
        draft={refine.draft}
        rewriting={refine.rewriting}
        posting={refine.posting}
        error={refine.error}
        onDraftChange={(t) => setSubmission((s) => (s.phase === 'refine' ? { ...s, draft: t } : s))}
        onVoiceChange={form.setVoice}
        onRewrite={onRewrite}
        onPost={onPost}
        onBack={() => setSubmission({ phase: 'form', error: null })}
      />
    );
  }

  if (submission.phase === 'done') {
    return (
      <ResultScreen
        response={submission.response}
        onAnother={() => {
          form.reset();
          setFormKey((k) => k + 1);
          setSubmission({ phase: 'form', error: null });
        }}
      />
    );
  }

  const busy = submission.phase === 'submitting';
  const error = submission.phase === 'form' ? submission.error : null;

  return (
    <ReviewScreen
      key={formKey}
      form={form}
      busy={busy}
      error={error}
      onPostRating={postRating}
      onPostReview={startReview}
    />
  );
}
